import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faArrowLeft,
  faPlus,
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import {
  GeneralTaskApi,
  GeneralTaskDTO,
  GeneralTaskReportDTO,
} from './GeneralTaskApi';
import GeneralTaskActions from './GeneralTaskAction';
import { TaskStatusOptions, TASK_PRIORITIES } from '../../constants/taskConstants';

const user = JSON.parse(localStorage.getItem('user') || '{}');
const tenantId = user?.tenantId || '';
const tenantName = user?.tenantName || '';
const userId = user?.id || '';

const getDefaultFilters = (): GeneralTaskDTO => ({
  id: '',
  tenantId,
  status: '',
  priority: '',
  title: '',
  description: '',
});

const STATUS_OPTIONS = TaskStatusOptions.map(option => option.value);
const PRIORITY_OPTIONS = TASK_PRIORITIES.map(option => option.value);

const GeneralTaskList: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<GeneralTaskDTO>(getDefaultFilters());
  const [tasks, setTasks] = useState<GeneralTaskReportDTO[]>([]);
  const [expandedTaskIds, setExpandedTaskIds] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getEntityPath = (entityType: string, entityId: string) => {
    switch (entityType) {
      case 'PROJECT':
        return `/projects/${entityId}`;
      case 'CONTRACT':
        return `/contracts/${entityId}`;
      case 'PROJECT_TASK':
        return `/project-tasks/${entityId}`;
      case 'GENERAL_TASK':
        return `/general-tasks/${entityId}`;
      case 'VEHICLE':
        return `/vehicles/${entityId}`;
      case 'INSURANCE':
        return `/insurances/${entityId}`;
      case 'INVENTORY':
        return `/inventories/${entityId}`;
      case 'GST':
        return `/gst/${entityId}`;
      default:
        return '';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const response = await GeneralTaskApi.searchWithSubtask(filters);
      setTasks(response.data.content || []);
    } catch (err) {
      setError('Failed to fetch tasks.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    const resetFilters = getDefaultFilters();
    setFilters(resetFilters);
    setTimeout(() => {
      handleSearch();
    }, 0);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this task?')) {
      await GeneralTaskApi.delete(id);
      handleSearch();
    }
  };

  const handleCreateTask = (parentTask: GeneralTaskDTO, levelNumber: string) => {
    navigate('/general-tasks/create', {
      state: {
        parentId: parentTask.id,
        title: parentTask.title,
        description: parentTask.description,
        status: parentTask.status,
        levelNumber,
      },
    });
  };

  const toggleExpand = (taskId: string) => {
    setExpandedTaskIds((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const renderNestedTasks = (
    taskList: GeneralTaskReportDTO[],
    level = 1,
    prefix = ''
  ): JSX.Element[] => {
    return taskList.flatMap((task, index) => {
      const levelNumber = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
      const indent = level * 20;
      const isExpanded = expandedTaskIds[task.id];
      const hasChildren = task.children?.length > 0;
      const maxLevelReached = level >= 4;

      const row = (
        <tr key={task.id}>
          <td className="p-2 border text-center">
            {hasChildren && (
              <button onClick={() => toggleExpand(task.id)}>
                <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
              </button>
            )}
          </td>
          <td className="p-2 border">{levelNumber}</td>

          <GeneralTaskActions
            task={task as GeneralTaskReportDTO}
            levelNumber={levelNumber}
            maxLevelReached={maxLevelReached}
            handleDelete={handleDelete}
            handleCreateTask={handleCreateTask}
          />

          <td className="p-2 border" style={{ paddingLeft: `${indent}px` }}>
            {task.title}
          </td>
          <td className="p-2 border">{task.description}</td>
          <td className="p-2 border">{task.status}</td>
          <td className="p-2 border">{task.priority}</td>
          <td className="p-2 border text-blue-600 underline cursor-pointer">
              {task.entityId && task.entityType ? (
                <button onClick={() => task.entityType && task.entityId && navigate(getEntityPath(task.entityType, task.entityId))}>
                  {task.entityType}
                </button>
              ) : (
                task.entityType || '-'
              )}
            </td>
          <td className="p-2 border">{task.taskType}</td>
          <td className="p-2 border">{task.assignedToName}</td>
          <td className="p-2 border">{task.estimatedHours}</td>
          <td className="p-2 border">{task.actualHours}</td>
          <td className="p-2 border">{task.dueDate}</td>
        </tr>
      );

      const childrenRows =
        hasChildren && isExpanded
          ? renderNestedTasks(task.children, level + 1, levelNumber)
          : [];

      return [row, ...childrenRows];
    });
  };

  useEffect(() => {
    handleSearch();
  }, []);

  return (
    <div className="p-4">
      <button onClick={() => navigate(-1)} className="mb-4 text-blue-600 hover:underline">
        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back
      </button>

      <h2 className="text-xl font-bold mb-4">General Task Search</h2>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <input
          name="title"
          value={filters.title || ''}
          onChange={handleInputChange}
          placeholder="Title"
          className="border px-3 py-2 rounded w-full"
        />
        <input
          name="description"
          value={filters.description || ''}
          onChange={handleInputChange}
          placeholder="Description"
          className="border px-3 py-2 rounded w-full"
        />
        <select
          name="status"
          value={filters.status || ''}
          onChange={handleInputChange}
          className="border px-3 py-2 rounded w-full"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
        <select
          name="priority"
          value={filters.priority || ''}
          onChange={handleInputChange}
          className="border px-3 py-2 rounded w-full"
        >
          <option value="">All Priorities</option>
          {PRIORITY_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <div className="md:col-span-4 flex gap-2">
          <button
            onClick={handleSearch}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            <FontAwesomeIcon icon={faSearch} className="mr-2" /> Apply Filters
          </button>
          <button
            onClick={handleReset}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Reset
          </button>
          <button
            onClick={() => navigate('/general-tasks/create')}
            className="ml-auto bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Create General Task
          </button>
        </div>
      </div>

      {/* Task Table */}
      <div className="overflow-x-auto">
        <table className="w-full border border-gray-200 text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 border text-center">▶</th>
              <th className="p-2 border">Level</th>
              <th className="p-2 border">Actions</th>
              <th className="p-2 border">Title</th>
              <th className="p-2 border">Description</th>
              <th className="p-2 border">Status</th>
              <th className="p-2 border">Priority</th>
              <th className="p-2 border">Entity Type</th>
              <th className="p-2 border">Task Type</th>
              <th className="p-2 border">Assigned To</th>
              <th className="p-2 border">Estimated Hours</th>
              <th className="p-2 border">Actual Hours</th>
              <th className="p-2 border">Due Date</th>
            </tr>
          </thead>
          <tbody>{renderNestedTasks(tasks)}</tbody>
        </table>
      </div>
    </div>
  );
};

export default GeneralTaskList;